import React, { useState } from 'react';
import styled from 'styled-components';

import SubNavBar from 'features/banking/agentBanking/components/subNav';
import { Route, Redirect, Switch } from 'react-router';
import {
  DepositAcceptModal,
  DepositDeclineModal,
  DepositNewModal,
} from 'features/banking/agentBanking/components/depositModalCards';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { defaultModalChoicesState } from 'features/banking/agentBanking/localState/states';
import { transferChoicesRecipe } from 'features/banking/agentBanking/localState/recipes';
import {
  doGetTransferChoices,
  doGetTransferRequestChoices,
  doGetAccountTransferChoices,
} from 'features/banking/agentBanking/localState/thunkActions';
import {
  WithdrawalNewModal,
  WithdrawalCancelModal,
  WithdrawalCompleteModal,
  WithdrawalTransferModal,
} from 'features/banking/agentBanking/components/withdrawModalCards';
import {
  TransferAcceptModal,
  TransferDeclineModal,
  TransferNewModal,
} from './components/transferModalCards';
import FadeIn from 'react-fade-in';
import { ThemedModal } from 'common/components/cardStyles';
import { DepositsTable } from './components/depositsTable';
import { WithdrawalsTable } from './components/widthdrawalsTable';
import {
  ClientTransfer,
  ClientTransferRequest,
} from 'common/state/banking/transfers/actions';
import { AccountTransfersTable } from './components/accountTransfersTable';
import { AccountTransfer } from 'common/state/accounts/transfers/actions';

const BackgroundContainer = styled.div`
  flex: 1;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  margin: 1rem;
`;

export type ModalMode =
  | null
  | 'depositNew'
  | 'depositAccept'
  | 'depositDecline'
  | 'withdrawalNew'
  | 'withdrawalComplete'
  | 'withdrawalTransfer'
  | 'withdrawalCancel'
  | 'transferNew'
  | 'transferAccept'
  | 'transferDecline';

function AgentBanking() {
  const [selectedDepositRow, setSelectedDepositRow] =
    useState<ClientTransfer>(null);
  const [selectedWithdrawalRow, setSelectedWithdrawalRow] =
    useState<ClientTransferRequest>(null);
  const [selectedTransferRow, setSelectedTransferRow] =
    useState<AccountTransfer>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);

  const closeModal = React.useCallback(() => setModalMode(null), []);

  /**
   * We need to track if a modal successfully updated the database
   * If it did, we need to refresh the table
   * If the modal was a success, reset it to false for the next modal and trigger
   * A re-render for the Table routes
   */
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);
  React.useEffect(() => setShouldRefresh(false), [shouldRefresh]);

  /**
   * For the Modals, we need to first retrieve the values for the Select components
   * To do this we need to parse an OPTIONs call to /transfer/, /transfer_request and /account_transfer/
   */

  const [choices, dispatch] = useProducerWithThunks(
    transferChoicesRecipe,
    defaultModalChoicesState
  );
  const { depositChoices, withdrawalChoices, transferChoices } = choices;

  React.useEffect(() => {
    dispatch(doGetTransferChoices());
    dispatch(doGetTransferRequestChoices());
    dispatch(doGetAccountTransferChoices());
  }, [dispatch]);

  return (
    <BackgroundContainer>
      <SubNavBar />
      <Container>
        <Switch>
          <Route path="/banking/deposits">
            {!shouldRefresh && (
              <FadeIn>
                <DepositsTable
                  setSelectedRow={setSelectedDepositRow}
                  setModalMode={setModalMode}
                />
              </FadeIn>
            )}
          </Route>
          <Route path="/banking/withdrawals">
            {!shouldRefresh && (
              <FadeIn>
                <WithdrawalsTable
                  setSelectedRow={setSelectedWithdrawalRow}
                  setModalMode={setModalMode}
                />
              </FadeIn>
            )}
          </Route>
          <Route path="/banking/transfers">
            {!shouldRefresh && (
              <FadeIn>
                <AccountTransfersTable
                  setSelectedRow={setSelectedTransferRow}
                  setModalMode={setModalMode}
                />
              </FadeIn>
            )}
          </Route>
          <Route render={() => <Redirect to="/banking/deposits" />} />
        </Switch>
      </Container>

      <ThemedModal open={modalMode !== null}>
        {modalMode === 'depositNew' && choices && (
          <FadeIn>
            <DepositNewModal
              choices={depositChoices}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'depositAccept' && (
          <FadeIn>
            <DepositAcceptModal
              selectedRow={selectedDepositRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'depositDecline' && (
          <FadeIn>
            <DepositDeclineModal
              selectedRow={selectedDepositRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'withdrawalNew' && (
          <FadeIn>
            <WithdrawalNewModal
              choices={withdrawalChoices}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'withdrawalComplete' && (
          <FadeIn>
            <WithdrawalCompleteModal
              selectedRow={selectedWithdrawalRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'withdrawalTransfer' && (
          <FadeIn>
            <WithdrawalTransferModal
              choices={depositChoices}
              selectedRow={selectedWithdrawalRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'withdrawalCancel' && (
          <FadeIn>
            <WithdrawalCancelModal
              selectedRow={selectedWithdrawalRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'transferNew' && (
          <FadeIn>
            <TransferNewModal
              choices={transferChoices}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'transferAccept' && (
          <FadeIn>
            <TransferAcceptModal
              selectedRow={selectedTransferRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
        {modalMode === 'transferDecline' && (
          <FadeIn>
            <TransferDeclineModal
              selectedRow={selectedTransferRow}
              setShouldRefresh={setShouldRefresh}
              closeModal={closeModal}
            />
          </FadeIn>
        )}
      </ThemedModal>
    </BackgroundContainer>
  );
}

export default AgentBanking;
